
import React from 'react';
import { UIProvider } from './contexts/UIProvider';
import { AuthProvider, useAuth } from './contexts/AuthProvider';
import { NavigationProvider, useNavigation } from './contexts/NavigationProvider';
import { OpportunitiesProvider } from './contexts/OpportunitiesProvider';
import { MessagingProvider } from './contexts/MessagingProvider';
import { NotificationsProvider } from './contexts/NotificationsProvider';
import { LanguageProvider } from './contexts/LanguageProvider';

// Components
import Header from './components/Header';
import LeftSidebar from './components/LeftSidebar';
import RightSidebar from './components/RightSidebar';
import BottomNavBar from './components/BottomNavBar';
import MobileMenu from './components/MobileMenu';
import SplashScreen from './components/SplashScreen';
import AuthPage from './components/AuthPage';
import OnboardingPage from './components/OnboardingPage';
import Feed from './components/Feed';
import ProfilePage from './components/ProfilePage';
import MyActivityPage from './components/MyActivityPage';
import ApplicationDetailPage from './components/ApplicationDetailPage';
import OpportunityDetailPage from './components/OpportunityDetailPage';
import ApplicantListPage from './components/ApplicantListPage';
import CreatePost from './components/CreatePost';
import MyNetworkPage from './components/MyNetworkPage';
import MessagesPage from './components/MessagesPage';
import PumpUpTheVolumePage from './components/PumpUpTheVolumePage';
import NotificationsPage from './components/NotificationsPage';
import FeedbackPage from './components/FeedbackPage';
import LegalPage from './components/LegalPage';
import RecommendedPage from './components/RecommendedPage';
import MyCalendarPage from './components/MyCalendarPage';
import BandPage from './components/BandPage';
import SettingsPage from './components/SettingsPage';
import MyProjectsPage from './components/MyProjectsPage';
import MyLineupsPage from './components/MyLineupsPage';
import MyFlyersPage from './components/MyFlyersPage';
import LineupEditorPage from './components/LineupEditorPage';
import FlyerPreviewPage from './components/FlyerPreviewPage';
import ProfileManagerPage from './components/ProfileManagerPage';
import AdminDashboard from './components/AdminDashboard';
import ProDashboard from './components/ProDashboard';
import SearchPage from './components/SearchPage';
import SearchResultsView from './components/SearchResultsView';
import SessionsPage from './components/SessionsPage';
import Toast from './components/Toast';
import ManagementBanner from './components/ManagementBanner';
import UpgradeModal from './components/UpgradeModal';
import EditProfilePage from './components/EditProfilePage';
import ManageBandPage from './components/ManageBandPage';
import FanListPage from './components/FanListPage';
import EndorserListPage from './components/EndorserListPage';
import Footer from './components/Footer';
import EmailPendingPage from './components/EmailPendingPage';
import EmailVerifyPage from './components/EmailVerifyPage';
import PublicLandingPage from './components/PublicLandingPage';
import ForgotPasswordPage from './components/ForgotPasswordPage';
import ResetPasswordPage from './components/ResetPasswordPage';

const PAGE_TITLES: Partial<Record<string, string>> = {
    feed:            'Feed | beYourAMP',
    search:          'Search | beYourAMP',
    network:         'My Network | beYourAMP',
    messages:        'Messages | beYourAMP',
    notifications:   'Notifications | beYourAMP',
    myActivity:      'My Activity | beYourAMP',
    recommended:     'Recommended | beYourAMP',
    calendar:        'Calendar | beYourAMP',
    proDashboard:    'Pro Dashboard | beYourAMP',
    settings:        'Settings | beYourAMP',
    myProjects:      'My Projects | beYourAMP',
    myBands:         'My Bands | beYourAMP',
    myLineups:       'My Lineups | beYourAMP',
    myFlyers:        'My Flyers | beYourAMP',
    pumpUpTheVolume: 'Boost | beYourAMP',
    terms:           'Terms of Service | beYourAMP',
    privacy:         'Privacy Policy | beYourAMP',
    cookies:         'Cookie Policy | beYourAMP',
    feedback:        'Feedback | beYourAMP',
};

const MainContent: React.FC = () => {
    const { view, searchTerm, viewingProfile, viewingOpportunity } = useNavigation();

    React.useEffect(() => {
        if (view === 'profile' && viewingProfile) {
            document.title = `${viewingProfile.name} | beYourAMP`;
        } else if (view === 'opportunityDetail' && viewingOpportunity) {
            document.title = `${viewingOpportunity.title} | beYourAMP`;
        } else if (PAGE_TITLES[view]) {
            document.title = PAGE_TITLES[view]!;
        } else {
            document.title = 'beYourAMP | The Professional Network for Music & Entertainment';
        }
    }, [view, viewingProfile, viewingOpportunity]);

    if (searchTerm.trim() !== '' && view !== 'search') {
        return <SearchResultsView />;
    }

    switch (view) {
        case 'feed': return <Feed />;
        case 'profile': return <ProfilePage />;
        case 'editProfile': return <EditProfilePage />;
        case 'manageBand': return <ManageBandPage />;
        case 'myActivity': return <MyActivityPage />;
        case 'applicationDetail': return <ApplicationDetailPage />;
        case 'opportunityDetail': return <OpportunityDetailPage />;
        case 'applicantList': return <ApplicantListPage />;
        case 'createOpportunity': return <CreatePost />;
        case 'editOpportunity': return <CreatePost />;
        case 'network': return <MyNetworkPage />;
        case 'messages': return <MessagesPage />;
        case 'pumpUpTheVolume': return <PumpUpTheVolumePage />;
        case 'notifications': return <NotificationsPage />;
        case 'feedback': return <FeedbackPage />;
        case 'terms':
        case 'privacy':
        case 'cookies': return <LegalPage />;
        case 'recommended': return <RecommendedPage />;
        case 'calendar': return <MyCalendarPage />;
        case 'bandPage': return <BandPage />;
        case 'settings': return <SettingsPage />;
        case 'myProjects': return <MyProjectsPage />;
        case 'myLineups': return <MyLineupsPage />;
        case 'myFlyers': return <MyFlyersPage />;
        case 'lineupEditor': return <LineupEditorPage />;
        case 'flyerPreview': return <FlyerPreviewPage />;
        case 'profileManager': return <ProfileManagerPage />;
        case 'adminDashboard': return <AdminDashboard />;
        case 'proDashboard': return <ProDashboard />;
        case 'search': return <SearchPage />;
        case 'sessions': return <SessionsPage />;
        case 'fanList': return <FanListPage />;
        case 'endorserList': return <EndorserListPage />;
        case 'emailVerify': return <EmailVerifyPage />;
        default: return <Feed />;
    }
};

const AppShell: React.FC = () => {
    const { authStatus, currentUser } = useAuth();
    const { view } = useNavigation();
    const [showAuth, setShowAuth]               = React.useState(false);
    const [showForgotPassword, setShowForgotPassword] = React.useState(false);

    if (authStatus === 'checking') return <SplashScreen />;
    if (authStatus === 'unauthenticated') {
        const params = new URLSearchParams(window.location.search);
        const urlView = params.get('v');
        const resetToken  = params.get('token');
        const resetHandle = params.get('h');

        if (urlView === 'resetPassword' && resetToken && resetHandle) {
            return (
                <ResetPasswordPage
                    token={resetToken}
                    handle={resetHandle}
                    onDone={() => {
                        window.history.replaceState({}, '', window.location.pathname);
                        setShowAuth(true);
                    }}
                />
            );
        }

        if (showForgotPassword) {
            return <ForgotPasswordPage onBack={() => setShowForgotPassword(false)} />;
        }

        if (showAuth) {
            return (
                <AuthPage
                    onBack={() => setShowAuth(false)}
                    onForgotPassword={() => { setShowAuth(false); setShowForgotPassword(true); }}
                />
            );
        }

        return <PublicLandingPage onShowAuth={() => setShowAuth(true)} />;
    }
    if (currentUser?.needsOnboarding) return <OnboardingPage />;
    if (currentUser?.emailVerified === false) {
        const isVerifyLink = new URLSearchParams(window.location.search).get('v') === 'emailVerify';
        return isVerifyLink ? <EmailVerifyPage /> : <EmailPendingPage />;
    }

    const isFullscreenView = ['flyerPreview', 'lineupEditor'].includes(view);

    return (
        <div className="min-h-screen bg-brand-dark flex flex-col">
            <ManagementBanner />
            {!isFullscreenView && <Header />}
            
            <div className={`flex-1 flex flex-col md:flex-row max-w-7xl mx-auto w-full px-0 sm:px-4 lg:px-8 ${isFullscreenView ? 'p-0 m-0 max-w-none' : 'py-6 md:py-8'}`}>
                {!isFullscreenView && (
                    <div className="hidden md:block w-64 lg:w-72 flex-shrink-0">
                        <div className="sticky top-24">
                            <LeftSidebar />
                        </div>
                    </div>
                )}

                <main className={`flex-1 min-w-0 ${isFullscreenView ? 'p-0' : 'px-4 md:px-8'}`}>
                    <MainContent />
                </main>

                {!isFullscreenView && (
                    <div className="hidden lg:block w-80 flex-shrink-0">
                        <div className="sticky top-24">
                            <RightSidebar />
                        </div>
                    </div>
                )}
            </div>

            {!isFullscreenView && <Footer />}
            {!isFullscreenView && <BottomNavBar />}
            <MobileMenu />
            <Toast />
            <UpgradeModal />
        </div>
    );
};

export default function App() {
    return (
        <UIProvider>
            <AuthProvider>
                <LanguageProvider>
                    <NavigationProvider>
                        <OpportunitiesProvider>
                            <NotificationsProvider>
                                <MessagingProvider>
                                    <AppShell />
                                </MessagingProvider>
                            </NotificationsProvider>
                        </OpportunitiesProvider>
                    </NavigationProvider>
                </LanguageProvider>
            </AuthProvider>
        </UIProvider>
    );
}